import { PvpFlag, pvpUnlocked } from "odyssey-shared";
import { say } from "../../words.ts";
import { PAGE_RULES } from "../rules.ts";
import { flagToggleView, toggleDelays } from "../hud/flag-toggle.ts";
import type { HudSnapshot } from "../hud/store.ts";

/**
 * What the invitation prompt says of the party's flag (ADR-076's amendment of 2026-10-08, E3, with the build note of
 * 2026-10-15): the sentence above the buttons and what the Accept button offers. The page never decides a switch; it
 * reads what the HUD already knows (the flag byte, the preference, the level, the Combatant window, the switch that
 * counts) the way the flag panel does, so the prompt and the panel agree. The rules live here apart from the component
 * so they are tested without React.
 */

/**
 * What the Accept button offers: `accept` the plain Accept, `switch` the enabled "Switch and accept", `wait` the same
 * label held disabled until the block passes or the switch ends, and `none` no Accept at all (Decline only).
 */
export type PromptAccept = "accept" | "switch" | "wait" | "none";

/**
 * The prompt's flag part: the line above the buttons (null for none), whether it is a warning, and the Accept's offer.
 */
export interface PromptFlagView {
  readonly line: string | null;
  readonly warning: boolean;
  readonly accept: PromptAccept;
}

/**
 * The HUD facts the view reads, and the page clock the Combatant and switch countdowns count on.
 */
export type PromptFlagFacts = Pick<HudSnapshot, "flag" | "preference" | "combatantEndsAt" | "pvpSwitch" | "level" | "vitals"> & { readonly nowMs: number };

/**
 * The switch's delays from the page's rules.
 */
const DELAYS = toggleDelays(PAGE_RULES.constants);

/**
 * The plain Accept: nothing to say about the flag.
 */
const PLAIN: PromptFlagView = { line: null, warning: false, accept: "accept" };

/**
 * What the prompt says of the party's flag.
 *
 * An unknown flag (2, the founder was not read, or a value this build does not know) says so under a plain Accept: the
 * page never guesses a flag it was not told. A party whose flag the player already holds reads as the plain Accept, or,
 * while a switch counts away from it, says joining cancels that switch (the map server drops it as the roster arrives).
 * Otherwise the player
 * must switch to the party's flag before the seat: a Flagged party below the rules' `pvp_level` offers Decline only;
 * a block the flag panel also shows (a Hostile zone, a Combatant window, combat, the need to reach a safe zone) holds
 * the Accept with the panel's line; a switch already counting toward the party's flag shows its countdown; else
 * "Switch and accept" is offered with what the switch takes.
 *
 * @param partyFlag `partyInvited.flag`: 0 Protected, 1 Flagged, 2 unknown.
 * @param facts What the HUD knows of the player and the page clock.
 * @returns The line, whether it warns, and the Accept's offer.
 * @example
 * promptFlagView(1, { flag: 0, preference: 0, combatantEndsAt: null, pvpSwitch: { endsAt: null, toward: 0, note: 0 }, level: 12, vitals: null, nowMs: 0 });
 * // { line: "To accept, you'll need to Flag", warning: false, accept: "switch" }
 */
export function promptFlagView(partyFlag: number, facts: PromptFlagFacts): PromptFlagView {
  if (partyFlag !== PvpFlag.Protected && partyFlag !== PvpFlag.Flagged) {
    return { line: say("party.prompt.unknown"), warning: false, accept: "accept" };
  }
  if (facts.preference === partyFlag) {
    return facts.pvpSwitch.endsAt === null ? PLAIN : { line: say("party.prompt.switchCancelled"), warning: false, accept: "accept" };
  }
  const required = PAGE_RULES.constants.pvp_level;
  const view = flagToggleView({ nowMs: facts.nowMs, flag: facts.flag, preference: facts.preference, combatantEndsAt: facts.combatantEndsAt, inCombat: facts.vitals?.inCombat ?? false, dead: facts.vitals?.dead ?? false, pvpSwitch: facts.pvpSwitch }, DELAYS);
  if ((view.state === "to-flagged" || view.state === "to-protected") && facts.pvpSwitch.toward === partyFlag) {
    return { line: view.line, warning: false, accept: "wait" };
  }
  if (partyFlag === PvpFlag.Flagged && facts.level !== null && !pvpUnlocked(facts.level, required)) {
    return { line: say("party.prompt.level", { level: String(required) }), warning: true, accept: "none" };
  }
  if (view.state === "unsafe") {
    return { line: say("party.prompt.unsafe"), warning: true, accept: "wait" };
  }
  if (view.locked) {
    return { line: view.line, warning: true, accept: "wait" };
  }
  return { line: say(partyFlag === PvpFlag.Flagged ? "party.prompt.needFlag" : "party.prompt.needUnflag"), warning: false, accept: "switch" };
}
